import type { FullscreenPort } from '@Domain/Ports/Fullscreen/FullscreenPort';
import documentFullscreenSignal, {
	setDocumentFullscreen,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentFullscreenSignal';
import useFullscreenPort from '@UI/Providers/PortsProvider/Hooks/Fullscreen/useFullscreenPort';
import { useSignalValue } from '@verclam/pulsar';
import * as React from 'react';

export type DocumentFullscreen = {
	readonly isFullscreen: boolean;
	/** Must run synchronously inside a user gesture: the browser refuses fullscreen otherwise. */
	readonly enter: () => void;
	readonly exit: () => void;
};

const requestNativeEnter = (port: FullscreenPort): void => {
	if (port.isSupported()) {
		void port.enter();
	}
};

/** Reads and drives the screen's fullscreen mode; the native exit follows in `useFullscreenSync`. */
const useDocumentFullscreen = (): DocumentFullscreen => {
	const port = useFullscreenPort();
	const isFullscreen = useSignalValue(documentFullscreenSignal);

	const enter = React.useCallback(() => {
		setDocumentFullscreen(true);
		requestNativeEnter(port);
	}, [port]);

	const exit = React.useCallback(() => {
		setDocumentFullscreen(false);
	}, []);

	return { isFullscreen, enter, exit };
};

export default useDocumentFullscreen;
